export type AnalyticsEventName =
  | "add_to_cart"
  | "begin_checkout"
  | "checkout_error"
  | "cta_click"
  | "docs_click"
  | "github_click"
  | "main_site_click"
  | "outbound_click";

export type AnalyticsProperties = Record<
  string,
  boolean | number | string | null | undefined
>;

type CleanAnalyticsProperties = Record<string, boolean | number | string>;

type PlausibleOptions = {
  readonly props?: CleanAnalyticsProperties;
};

type Z2VTrackOptions = {
  readonly pageView?: boolean;
};

type Z2VTrackClient = {
  init: (options: Z2VTrackOptions) => void;
  track: (
    eventName: string,
    properties?: CleanAnalyticsProperties,
  ) => Promise<Response>;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    plausible?: (eventName: string, options?: PlausibleOptions) => void;
    Z2VTrack?: Z2VTrackClient;
  }
}

export const PLAUSIBLE_SCRIPT_SRC =
  "https://plausible.io/js/pa-FKacSuykz7ZSFIeY7Jihr.js";
export const GOOGLE_TAG_ID = "G-15QZYK2DYN";

export const STRICT_CONSENT_REGION_CODES = [
  "AT",
  "BE",
  "BG",
  "CH",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GB",
  "GR",
  "HR",
  "HU",
  "IE",
  "IS",
  "IT",
  "LI",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK",
  "UK",
] as const;

const cleanProperties = (
  properties: AnalyticsProperties,
): CleanAnalyticsProperties => {
  return Object.fromEntries(
    Object.entries(properties).filter(
      (entry): entry is [string, boolean | number | string] =>
        entry[1] !== null && entry[1] !== undefined,
    ),
  );
};

export const updateGoogleConsent = (value: "denied" | "granted"): void => {
  if (typeof window === "undefined" || !window.gtag) {
    return;
  }

  window.gtag("consent", "update", {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
};

export const trackEvent = (
  eventName: AnalyticsEventName,
  properties: AnalyticsProperties = {},
): void => {
  if (typeof window === "undefined") {
    return;
  }

  const clean = cleanProperties({
    page_path: window.location.pathname,
    ...properties,
  });

  window.plausible?.(eventName, { props: clean });
  void window.Z2VTrack?.track(eventName, clean).catch(() => undefined);

  if (!window.gtag) {
    return;
  }

  window.gtag("event", eventName, clean);
};
